import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Copy, FileText, Search, Filter } from 'lucide-react';
import { deleteTask, getSavedTasks } from '../../../utils/APIs';
import SaveDuplicateTaskModal from '../../../components/FunctionDetails/Modal/SaveDuplicateTaskModal';
import SavedTaskLogsModal from '../../../components/UserManagement/SavedTaskLogsModal';
import SuccessAlert from '../../../components/UI/Alert/SuccessAlert';
import ErrorAlert from '../../../components/UI/Alert/ErrorAlert';
import { useUserContext } from '../../../contexts/UserContext';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import { permission } from '../../../config/permission';
import ConfirmModal from '../../../components/UI/Modal/confirmModal';
import { AgTableCard, Input, SegmentedControl } from '../../../components/UI';

const SavedTasks = () => {
  const navigate = useNavigate();
  const { userData, checkPermission } = useUserContext();
  const { content } = useContentfulContext();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState('');
  const [showError, setShowError] = useState('');
  const [showLogId, setShowLogId] = useState(false);
  const [duplicateTaskModal, setDuplicateTaskModal] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [isAll, setIsAll] = useState(true);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getCombinedData();
  }, []);

  const getCombinedData = async () => {
    try {
      setLoading(true);
      const [savedTasks] = await Promise.all([
        getSavedTasks(userData.token, userData.selectedOrganization.org_token),
      ]);

      const combinedData = [
        ...savedTasks.data.map(task => ({ ...task, type: 'Adobe Task' })),
      ];
      setRowData(combinedData);
    } catch (error) {
      setShowError(error.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteTask(userData.token, userData.selectedOrganization.org_token, id);
      setShowSuccess('Task deleted successfully.');
      getCombinedData();
    } catch (error) {
      setShowError(error.message || 'Failed to delete task.');
    } finally {
      setLoading(false);
      setTaskToDelete(null);
      setShowConfirm(false);
    }
  };

  const handleDeleteClick = (row) => {
    if (row.is_recursive_task) {
      setTaskToDelete(row);
      setShowConfirm(true);
    } else {
      handleDelete(row._id);
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "saved_task_name", minWidth: 240 },
        { colId: "function_title", minWidth: 200 },
        { colId: "is_recursive_task", minWidth: 140 },
        { colId: "actions", minWidth: 120 }
      ]
    };
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: content.TASKS_CONTENT.saved_tasks.tasks_table_headers.name,
      field: 'saved_task_name',
      cellRenderer: (params) => {
        const { is_recursive_task, _id, adobe_io_integration_id, campaign_id, saved_task_name } = params.data;
        const func = params.data.function;

        let to = '';
        let state = {};
        if (func) {
          if (is_recursive_task) {
            to = `/functions/one-off-tasks/${func.id}/${_id}`;
          } else {
            to = `/functions/task-config/${func.id}`;
            state.saved_task_name = saved_task_name;
            state.saved_task_id = _id;
            state.funcId = func.id;
            state.adobe_io_integration_id = adobe_io_integration_id?._id;

            if (campaign_id) {
              state.campaign_id = campaign_id;
            }
          }
        }

        return (
          <Link
            to={to}
            state={{ ...state }}
            className="font-medium text-primary hover:text-primary-hover hover:underline"
          >
            {params.value}
          </Link>
        );
      },
      sortable: true,
      filter: true
    },
    {
      headerName: content.TASKS_CONTENT.saved_tasks.tasks_table_headers.task_name,
      field: 'function_title',
      cellRenderer: (params) => {
        const func = params.data.function;
        return <span className="text-stride-body text-sm">{func ? func.title : '—'}</span>;
      },
      valueGetter: (params) => params.data?.function?.title || '',
      sortable: true,
      filter: true
    },
    {
      headerName: content.TASKS_CONTENT.saved_tasks.tasks_table_headers.type,
      field: "is_recursive_task",
      cellRenderer: ({ value }) => (
        <span className={`stride-badge-pill font-medium text-xs ${value ? 'bg-primary-soft text-primary' : 'bg-slate-100 text-slate-700'}`}>
          {value ? "Recurring" : "One-off"}
        </span>
      ),
      sortable: true,
      filter: true
    },
    {
      headerName: "Actions",
      field: 'actions',
      cellRenderer: (params) => {
        const row = params.data;
        const canEdit = checkPermission(permission.FUNCTIONS_SAVED_TASK_EDIT) && row.is_recursive_task;
        const canDelete = checkPermission(permission.FUNCTIONS_SAVED_TASK_DELETE);
        const canDuplicate = checkPermission(permission.FUNCTIONS_SAVED_TASK_DUPLICATE);

        return (
          <div className="stride-table-actions">
            {canEdit && (
              <button
                type="button"
                title="Edit Task"
                className="stride-table-btn stride-table-btn-edit"
                onClick={() => navigate(`/functions/one-off-tasks/${row?.function_id}/${row?._id}`)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {row.is_recursive_task && (
              <button
                type="button"
                title="View Logs"
                className="stride-table-btn hover:text-primary hover:border-primary/40"
                onClick={() => setShowLogId(row._id)}
              >
                <FileText className="w-3.5 h-3.5 text-primary" />
              </button>
            )}
            {canDuplicate && (
              <button
                type="button"
                title="Duplicate Task"
                className="stride-table-btn stride-table-btn-copy"
                onClick={() => setDuplicateTaskModal(row)}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title="Delete Task"
                className="stride-table-btn stride-table-btn-delete"
                onClick={() => handleDeleteClick(row)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
      width: 140,
      sortable: false,
      filter: false,
      resizable: false
    }
  ], [checkPermission, navigate, content]);

  const onFilterTextBoxChanged = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (gridApi) {
      gridApi.setFilterModel({
        saved_task_name: val ? { type: 'contains', filter: val.toLowerCase() } : null
      });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setFilterModel({});
    }
  };

  useEffect(() => {
    if (isAll) {
      setFilteredRowData(rowData);
    } else {
      setFilteredRowData(rowData.filter(task => task.is_recursive_task));
    }
  }, [isAll, rowData]);

  return (
    <>
      <main id="main" className="stride-page-shell flex flex-col space-y-6">
        <div>
          <span className="stride-eyebrow">Functions / Saved</span>
          <h1 id="saved-tasks-table" className="text-2xl font-bold tracking-tight text-stride-heading">
            {content.TASKS_CONTENT.saved_tasks.title}
          </h1>
          <p className="text-sm text-stride-muted mt-1">
            {content.TASKS_CONTENT.saved_tasks.description}
          </p>
        </div>

        {showSuccess && <SuccessAlert>{showSuccess}</SuccessAlert>}
        {showError && <ErrorAlert>{showError}</ErrorAlert>}

        <AgTableCard
          header={
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full">
              <div className="w-full md:w-80">
                <Input
                  placeholder="Search saved tasks..."
                  value={searchTerm}
                  onChange={onFilterTextBoxChanged}
                  prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                  clearable
                  onClear={handleClearSearch}
                />
              </div>

              {/* Filter Pills */}
              <SegmentedControl
                value={isAll ? 'all' : 'recurring'}
                onChange={(val) => setIsAll(val === 'all')}
                variant="primary"
                size="md"
                options={[
                  { value: 'all', label: `All Tasks (${rowData?.length || 0})` },
                  { value: 'recurring', label: `Recurring Only (${rowData?.filter(r => r.is_recursive_task)?.length || 0})`, icon: <Filter className="w-3.5 h-3.5" /> },
                ]}
              />
            </div>
          }
          tableId="saved-tasks-table"
          loading={loading}
          rowData={filteredRowData}
          columnDefs={columnDefs}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
          domLayout="autoHeight"
        />

        {showConfirm && taskToDelete && (
          <ConfirmModal
            title="Confirm Deletion"
            description="Deleting the recurring task will remove all task logs permanently. Are you sure you want to continue?"
            ConfirmText="Delete"
            error={showError}
            loading={loading}
            hideModal={setShowConfirm}
            handleConfirmation={(confirmed) => {
              if (confirmed) {
                handleDelete(taskToDelete._id);
              } else {
                setShowConfirm(false);
                setTaskToDelete(null);
              }
            }}
          />
        )}

        {duplicateTaskModal && (
          <SaveDuplicateTaskModal
            getSavedTasksData={getCombinedData}
            data={duplicateTaskModal}
            handleClose={() => setDuplicateTaskModal(null)}
          />
        )}

        {showLogId && <SavedTaskLogsModal setShowLogId={setShowLogId} showLogId={showLogId} />}
      </main>
    </>
  );
};

export default SavedTasks;
